import { useCallback, useState, useEffect } from 'react'
import useSWR, { Fetcher } from 'swr'
import { client } from '~/api/client'
import { Schemas, getCoordinateImageSearchKeys } from '~/api/gen'
import { ImageSearchResult } from '~/components/modules/ImageSearchResult/ImageSearchResult'
import {
  sendGtmEvItemImageSearchClose,
  sendGtmEvItemImageSearchNotFound,
} from '~/features/CoordinateDetail/lib/gtmEvent'
import { ImageSearchCloseType } from '~/features/shared/gtmEvent/types/coordinateDetail'
import { assertSuccess } from '~/lib/assertSuccess'

const fetchCoordinateImageSearchKeys: Fetcher<
  Schemas.image_search_key[],
  ['getCoordinateImageSearchKeys', number]
> = async ([_, coordinateId]) => {
  try {
    const res = await getCoordinateImageSearchKeys(client, {
      parameter: { coordinate_id: coordinateId.toString() },
    })
    assertSuccess(res)
    return res.data.image_search_keys
  } catch {
    return []
  }
}

type Props = {
  coordinateId: number
  imgUrl: string
  onCloseClick?: () => void
}

export const ImageSearch: React.FC<Props> = ({
  coordinateId,
  imgUrl,
  onCloseClick,
}) => {
  const { data, isValidating } = useSWR<
    Schemas.image_search_key[],
    Error,
    ['getCoordinateImageSearchKeys', number]
  >(
    ['getCoordinateImageSearchKeys', coordinateId],
    fetchCoordinateImageSearchKeys
  )
  const [imageContainerHeight, setImageContainerHeight] = useState(0)
  const [eventLabel, setEventLabel] = useState<string>('')

  useEffect(() => {
    setImageContainerHeight(window.innerWidth)
  }, [])

  useEffect(() => {
    if (!data || data.length > 0) return
    const eventLabelNotFound = 'notfound'
    setEventLabel(eventLabelNotFound)
    sendGtmEvItemImageSearchNotFound()
  }, [data])

  const closeButtonGTMEvent = useCallback(
    (eventElm: ImageSearchCloseType) => {
      if (!data) return
      sendGtmEvItemImageSearchClose(eventElm, eventLabel)
    },
    [data, eventLabel]
  )

  return (
    <ImageSearchResult
      imageContainerHeight={imageContainerHeight}
      imgUrl={imgUrl}
      isValidating={isValidating}
      coordinateId={coordinateId}
      imageSearchKeys={data}
      onCloseClick={onCloseClick}
      closeButtonGTMEvent={closeButtonGTMEvent}
      setEventLabel={setEventLabel}
    />
  )
}
